import { MixModal } from "@/components/core/modals/MixModal";
import {
    type AnonymousProfile,
    clearAnonymousProfile,
    generateAnonymousProfile,
    getAnonymousModeEnabled,
    getAnonymousProfile,
    saveAnonymousProfile,
    setAnonymousModeEnabled
} from "@/utils/anonymousProfile";
import Avatar from "boring-avatars";
import { RefreshCwIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface AnonymousProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (profile: AnonymousProfile, enabled: boolean) => void;
}

/**
 * AnonymousProfileModal Component
 *
 * Allows users to create/manage an anonymous profile for commenting
 * - Shows boring-avatars avatar with beam variant
 * - Generates random "AdjectiveAnimal" nickname
 * - Allows regeneration of profile with refresh button
 * - Saves profile to localStorage when confirmed
 * - Toggles anonymous mode on/off
 */
export const AnonymousProfileModal: React.FC<AnonymousProfileModalProps> = ({
    isOpen,
    onClose,
    onConfirm
}) => {
    const [profile, setProfile] = useState<AnonymousProfile>(() => {
        // Load existing profile or generate new one
        return getAnonymousProfile() || generateAnonymousProfile();
    });

    // Update profile when modal opens
    useEffect(() => {
        if (isOpen) {
            const existingProfile = getAnonymousProfile();
            if (existingProfile) {
                setProfile(existingProfile);
            } else {
                // Generate new profile if none exists
                setProfile(generateAnonymousProfile());
            }
        }
    }, [isOpen]);

    const handleRegenerate = useCallback(() => {
        const newProfile = generateAnonymousProfile();
        setProfile(newProfile);
    }, []);

    const handleConfirm = useCallback(() => {
        // Always enable anonymous mode when confirming
        saveAnonymousProfile(profile);
        setAnonymousModeEnabled(true);
        onConfirm(profile, true);
        onClose();
    }, [profile, onClose, onConfirm]);

    const handleCancel = useCallback(() => {
        // Reset to saved state
        const savedProfile = getAnonymousProfile();
        if (savedProfile) {
            setProfile(savedProfile);
        }
        onClose();
    }, [onClose]);

    return (
        <MixModal isOpen={isOpen} onRequestClose={handleCancel}>
            <div
                className="mx-4 w-full max-w-md rounded-2xl bg-white p-6
                    shadow-xl dark:bg-gray-800"
            >
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <h2
                        className="text-xl font-bold text-gray-900
                            dark:text-white"
                    >
                        익명 프로필
                    </h2>
                    <button
                        onClick={handleCancel}
                        className="p-1 text-gray-400 transition-colors
                            hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <XIcon className="size-5" />
                    </button>
                </div>

                {/* Profile Preview */}
                <div className="mb-6 flex flex-col items-center">
                    {/* Avatar */}
                    <div className="relative mb-4">
                        <Avatar
                            name={profile.avatarSeed}
                            variant="beam"
                            size={120}
                        />
                    </div>

                    {/* Regenerate Button */}
                    <button
                        onClick={handleRegenerate}
                        className="mb-4 flex items-center gap-2 rounded-lg
                            bg-gray-200 px-4 py-2 text-sm font-medium
                            text-gray-700 transition-colors hover:bg-gray-300
                            dark:bg-gray-700 dark:text-gray-300
                            dark:hover:bg-gray-600"
                    >
                        <RefreshCwIcon className="size-4" />
                        프로필 새로 만들기
                    </button>

                    {/* Nickname */}
                    <div className="text-center">
                        <p
                            className="mb-1 text-sm text-gray-500
                                dark:text-gray-400"
                        >
                            닉네임
                        </p>
                        <p
                            className="text-lg font-semibold text-gray-900
                                dark:text-white"
                        >
                            {profile.nickname}
                        </p>
                    </div>
                </div>

                {/* Description */}
                <div
                    className="mb-6 rounded-lg border border-blue-100 bg-blue-50
                        p-4 dark:border-blue-800 dark:bg-blue-900/20"
                >
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                        💡 확인을 누르면 익명 모드가 활성화되어 다른 사용자에게
                        프로필 사진과 이름 대신 익명 프로필이 표시됩니다.
                        언제든지 아이콘을 다시 클릭하여 익명 모드를 끌 수
                        있습니다.
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        onClick={handleCancel}
                        className="flex-1 rounded-lg bg-gray-100 px-4 py-3
                            text-sm font-medium text-gray-700 transition-colors
                            hover:bg-gray-200 dark:bg-gray-700
                            dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="flex-1 rounded-lg bg-blue-600 px-4 py-3
                            text-sm font-medium text-white transition-colors
                            hover:bg-blue-700"
                    >
                        확인
                    </button>
                </div>
            </div>
        </MixModal>
    );
};
